/**
 * BudgetSettingsPage (예산 설정 화면)
 *
 * 카테고리별 월 예산을 설정하는 페이지
 *
 * 책임:
 * - 템플릿 예산 로드 (year: null, month: null)
 * - 대분류/소분류 예산 입력 폼
 * - 예산 저장 (템플릿으로 저장하여 모든 월에 적용)
 * - 검증 (소분류 합계 ≤ 대분류 예산)
 *
 * 핵심 로직:
 * - 예산은 "템플릿"으로 저장됨 (year/month = null)
 * - 한 번 설정하면 모든 월에 자동 적용
 * - 특정 월에 다른 예산 설정 가능 (향후 확장)
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCategoryCRUD } from '@/domains/category/hooks/useCategoryCRUD'
import { useBudgets } from '../hooks/useBudgets'
import { useTransactions } from '@/domains/transaction/hooks/useTransactions'
import { budgetRepository } from '../repositories'
import { CategoryBudgetCard } from '../components/CategoryBudgetCard'
import { cascadeDeleteBudget } from '../utils/budgetHelpers'
import { useToast } from '@/shared/components/ui/Toast'
import { getMonthDateRange, getCurrentYearMonth } from '@/shared/utils/dateUtils'
import type { Budget } from '../types/Budget'

export function BudgetSettingsPage() {
  const navigate = useNavigate()
  const toast = useToast()

  /**
   * 카테고리 데이터 로드
   */
  const {
    data: allCategories,
    loading: categoriesLoading,
    loadByType,
  } = useCategoryCRUD()

  /**
   * 템플릿 예산 로드 (year: null, month: null)
   * 모든 년도/월에 적용되는 기본 예산
   */
  const { budgets, loading, error } = useBudgets({
    year: null,
    month: null,
    isActive: true,
  })

  /**
   * 최근 1개월 지출 데이터 로드
   * 목적: 실제 사용하는 카테고리 필터링
   */
  const [currentYear, currentMonth] = getCurrentYearMonth()
  const [startDate, endDate] = getMonthDateRange(currentYear, currentMonth)

  const { transactions, loading: transactionsLoading } = useTransactions({
    startDate,
    endDate,
    type: 'expense',
  })

  /**
   * 최근 1개월 지출이 있는 카테고리 ID 집합
   */
  const recentSpentCategoryIds = useMemo(() => {
    const ids = new Set<string>()
    transactions.forEach(tx => {
      ids.add(tx.majorCategoryId)
    })
    return ids
  }, [transactions])

  /**
   * 폼 상태
   * - budgetInputs: 대분류 예산 (categoryId -> amount)
   * - minorInputs: 소분류 예산 (minorCategoryId -> amount)
   * - expandedCategories: 펼쳐진 카테고리 Set
   * - saving: 저장 중 상태
   * - showAllCategories: "모든 카테고리 보기" 토글 상태
   */
  const [budgetInputs, setBudgetInputs] = useState<Record<string, number>>({})
  const [minorInputs, setMinorInputs] = useState<Record<string, number>>({})
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  )
  const [saving, setSaving] = useState(false)
  const [showAllCategories, setShowAllCategories] = useState(false)

  /**
   * 전체 예산 총합 계산
   */
  const totalBudget = useMemo(() => {
    return Object.values(budgetInputs).reduce((sum, amount) => sum + (amount || 0), 0)
  }, [budgetInputs])

  /**
   * 필터링된 카테고리 목록
   * 조건: (템플릿 예산이 있음) OR (최근 1개월 지출이 있음)
   *
   * "모든 카테고리 보기" 활성화 시 필터링 없이 전체 표시
   */
  const filteredCategories = useMemo(() => {
    const categories = allCategories.filter(
      category => category.type === 'expense' && !category.parentId
    )

    // "모든 카테고리 보기" 활성화 시 전체 표시
    if (showAllCategories) {
      return categories
    }

    // 기본: 예산 또는 최근 지출이 있는 것만
    return categories.filter(category => {
      const hasBudget = (budgetInputs[category.id] || 0) > 0
      const hasRecentSpent = recentSpentCategoryIds.has(category.id)
      return hasBudget || hasRecentSpent
    })
  }, [allCategories, budgetInputs, recentSpentCategoryIds, showAllCategories])

  /**
   * 지출 카테고리 로드
   */
  useEffect(() => {
    loadByType('expense')
  }, [])

  /**
   * 초기값 설정
   * 기존 템플릿 예산이 있으면 폼에 채워넣기
   */
  useEffect(() => {
    if (budgets.length > 0) {
      const initial: Record<string, number> = {}
      const minorInitial: Record<string, number> = {}

      budgets.forEach(b => {
        if (!b.parentBudgetId) {
          // 대분류 예산
          initial[b.categoryId] = b.amount
        } else {
          // 소분류 예산
          minorInitial[b.categoryId] = b.amount
        }
      })

      setBudgetInputs(initial)
      setMinorInputs(minorInitial)
    }
  }, [budgets])

  /**
   * 저장 핸들러
   *
   * 로직:
   * 1. 기존 템플릿 예산 모두 삭제
   * 2. 새로운 템플릿 예산 생성 (year: null, month: null)
   * 3. 대분류 생성 후 반환된 ID를 사용하여 소분류 생성
   */
  const handleSave = async () => {
    try {
      setSaving(true)

      // ✅ 디버깅: 현재 상태 출력
      console.log('=== 예산 저장 시작 ===')
      console.log('budgetInputs:', budgetInputs)
      console.log('minorInputs:', minorInputs)
      console.log('allCategories:', allCategories)

      // ✅ 소분류가 입력된 대분류의 예산 자동 계산
      const mergedBudgetInputs = { ...budgetInputs }

      for (const [minorId, minorAmount] of Object.entries(minorInputs)) {
        if (minorAmount && minorAmount > 0) {
          // 이 소분류의 부모 카테고리 찾기
          const minorCategory = allCategories
            .flatMap(c => c.children || [])
            .find(child => child.id === minorId)

          if (minorCategory?.parentId) {
            const parentId = minorCategory.parentId

            // 대분류 예산이 없으면 자동 계산 (소분류 합계)
            if (!mergedBudgetInputs[parentId]) {
              // 이 대분류의 모든 소분류 합계 계산
              const parentCategory = allCategories.find(c => c.id === parentId)
              const total = (parentCategory?.children || [])
                .reduce((sum, child) => {
                  return sum + (minorInputs[child.id] || 0)
                }, 0)

              mergedBudgetInputs[parentId] = total
              console.log(`✅ 대분류 자동 계산: ${parentId} = ${total}원 (소분류 합계)`)
            }
          }
        }
      }

      console.log('mergedBudgetInputs (자동 계산 적용):', mergedBudgetInputs)

      // 1. 기존 템플릿 예산 조회 (저장 시점에 다시 조회!)
      console.log('=== 기존 예산 조회 시작 ===')
      const existingBudgets = await budgetRepository.findAll({
        year: null,
        month: null,
        isActive: true,
      })
      console.log('기존 예산:', existingBudgets)

      // 2. categoryId별 기존 예산 맵 생성
      const existingMap = new Map<string, Budget>()
      for (const budget of existingBudgets) {
        existingMap.set(budget.categoryId, budget)
      }
      console.log('기존 예산 맵:', Array.from(existingMap.entries()))

      // 3. 대분류: Update 또는 Create
      for (const [categoryId, amount] of Object.entries(mergedBudgetInputs)) {
        if (amount <= 0) continue

        const existingMajor = existingMap.get(categoryId)
        let majorBudget: Budget

        if (existingMajor) {
          // 기존 예산 업데이트
          majorBudget = await budgetRepository.update(existingMajor.id, {
            amount,
          })
          console.log(`✅ 대분류 업데이트: ${categoryId} (${existingMajor.id}) = ${amount}원`)
        } else {
          // 새 예산 생성
          majorBudget = await budgetRepository.create({
            categoryId,
            amount,
            year: null,
            month: null,
            isActive: true,
          })
          console.log(`✅ 대분류 생성: ${categoryId} (${majorBudget.id}) = ${amount}원`)
        }

        // 4. 소분류: Update 또는 Create
        const category = allCategories.find(c => c.id === categoryId)
        if (category?.children) {
          console.log(`[${categoryId}] 소분류 처리 시작: ${category.children.length}개`)

          // 기존 소분류 예산 조회 (parentBudgetId 기준)
          const existingMinors = await budgetRepository.findAll({
            parentBudgetId: majorBudget.id,
            isActive: true,
          })
          console.log(`  기존 소분류 예산 ${existingMinors.length}개:`, existingMinors)

          const existingMinorMap = new Map<string, Budget>()
          for (const minor of existingMinors) {
            existingMinorMap.set(minor.categoryId, minor)
          }

          for (const minor of category.children) {
            const minorAmount = minorInputs[minor.id]

            if (minorAmount && minorAmount > 0) {
              const existingMinor = existingMinorMap.get(minor.id)

              if (existingMinor) {
                // 업데이트
                await budgetRepository.update(existingMinor.id, {
                  amount: minorAmount,
                })
                console.log(`  ✅ 소분류 업데이트: ${minor.id} (${existingMinor.id}) = ${minorAmount}원`)
              } else {
                // 생성
                await budgetRepository.create({
                  categoryId: minor.id,
                  parentBudgetId: majorBudget.id,
                  amount: minorAmount,
                  year: null,
                  month: null,
                  isActive: true,
                })
                console.log(`  ✅ 소분류 생성: ${minor.id} = ${minorAmount}원`)
              }
            } else {
              // 입력되지 않은 소분류는 비활성화
              const existingMinor = existingMinorMap.get(minor.id)
              if (existingMinor) {
                await budgetRepository.delete(existingMinor.id)
                console.log(`  ⏭️  소분류 비활성화: ${minor.id} (${existingMinor.id})`)
              }
            }
          }
        }
      }

      // 5. 입력되지 않은 대분류 비활성화 (cascade delete 적용)
      for (const [categoryId, existingBudget] of existingMap) {
        if (!mergedBudgetInputs[categoryId] || mergedBudgetInputs[categoryId] <= 0) {
          // Cascade delete: 대분류 삭제 시 모든 소분류도 함께 삭제
          const deletedIds = await cascadeDeleteBudget(budgetRepository, existingBudget.id)
          console.log(`🗑️  대분류 cascade 삭제: ${categoryId} (총 ${deletedIds.length}개 예산 삭제)`)
        }
      }

      console.log('=== 예산 저장 완료 ===')
      toast.success('예산이 저장되었습니다')
      navigate('/status')
    } catch (err) {
      console.error('예산 저장 실패:', err)
      toast.error(`저장 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    } finally {
      setSaving(false)
    }
  }

  /**
   * 취소 핸들러
   */
  const handleCancel = () => {
    navigate('/settings')
  }

  /**
   * 카테고리 펼침/접힘 토글
   */
  const handleToggle = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  /**
   * 로딩 상태 (budgets, categories, transactions 중 하나라도 로딩 중이면 true)
   */
  if (loading || categoriesLoading || transactionsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">로딩 중...</p>
        </div>
      </div>
    )
  }

  /**
   * 에러 상태
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-4">
            <span className="text-6xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            데이터를 불러올 수 없습니다
          </h2>
          <p className="text-red-600 text-sm text-center mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  /**
   * 메인 컨텐츠
   */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* 헤더 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <button
          onClick={handleCancel}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-2 flex items-center gap-1"
        >
          ← 뒤로 가기
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">예산 설정</h1>
      </div>

      {/* 안내 메시지 */}
      <div className="px-4 py-4 bg-primary-50 dark:bg-primary-900/20 border-b">
        <p className="text-sm text-primary-800 dark:text-primary-200">
          💡 설정한 예산은 모든 월에 자동으로 적용됩니다
        </p>
      </div>

      {/* 총 예산 표시 */}
      <div className="px-4 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">총 예산</span>
          <span className="text-lg font-bold text-gray-900 dark:text-white">
            {totalBudget.toLocaleString()}원
          </span>
        </div>
      </div>

      {/* 카테고리별 예산 입력 */}
      <div className="px-4 py-6 pb-32 space-y-3">
        {filteredCategories.length === 0 ? (
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              예산을 설정할 카테고리가 없습니다.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              지출을 등록하거나 모든 카테고리를 보려면 아래 버튼을 눌러주세요.
            </p>
          </div>
        ) : (
          filteredCategories.map(category => (
            <CategoryBudgetCard
              key={category.id}
              category={category}
              value={budgetInputs[category.id] || 0}
              onChange={val =>
                setBudgetInputs(prev => ({ ...prev, [category.id]: val }))
              }
              minorCategories={category.children || []}
              minorValues={minorInputs}
              onMinorChange={(minorId, val) =>
                setMinorInputs(prev => ({ ...prev, [minorId]: val }))
              }
              expanded={expandedCategories.has(category.id)}
              onToggle={() => handleToggle(category.id)}
            />
          ))
        )}
      </div>

      {/* "모든 카테고리 보기" 토글 버튼 */}
      <div className="fixed bottom-28 left-4 right-4 z-30">
        <button
          onClick={() => setShowAllCategories(prev => !prev)}
          className="w-full px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        >
          {showAllCategories ? '사용 중인 카테고리만 보기' : '모든 카테고리 보기'}
        </button>
      </div>

      {/* 저장/취소 버튼 (고정) - 하단 탭 위에 표시하기 위해 bottom-14 사용 */}
      <div className="fixed bottom-14 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg z-40">
        <div className="max-w-4xl mx-auto flex gap-3">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * 개발자 도구: 예산 상태 진단 및 orphan 정리
 *
 * 브라우저 개발자 콘솔에서 사용:
 * window.debugBudgets()
 *
 * 기능:
 * - 고아 예산 탐지 (parentBudgetId가 비활성 또는 존재하지 않는 예산을 가리키는 경우)
 * - 사용자 확인 후 고아 예산 정리 (isActive: false로 설정)
 */
if (import.meta.env.DEV) {
  (window as any).debugBudgets = async () => {
    const { findOrphanedBudgets, cleanupOrphanedBudgets } =
      await import('../utils/budgetHelpers')

    console.log('=== 예산 진단 시작 ===')
    const orphans = await findOrphanedBudgets(budgetRepository)

    if (orphans.length === 0) {
      console.log('✅ 고아 예산 없음')
      return
    }

    console.log(`⚠️  고아 예산 ${orphans.length}개 발견:`)
    orphans.forEach(({ budget, reason }) => {
      console.log(`  - ${budget.categoryId} (${budget.id}): ${reason}`)
    })

    const confirm = window.confirm(`${orphans.length}개의 고아 예산을 정리하시겠습니까?`)
    if (confirm) {
      const count = await cleanupOrphanedBudgets(budgetRepository)
      console.log(`✅ ${count}개 예산 정리 완료`)
      // window.debugBudgets는 컴포넌트 외부의 dev 전용 함수 — toast 컨텍스트 접근 불가하므로 alert 유지
      alert(`${count}개 예산 정리 완료`)
      // 페이지 새로고침하여 변경사항 반영
      window.location.reload()
    }
  }
}
